/**
 * FullDemoERP — primary navigation tree.
 *
 * This is the single source of truth for the sidebar and the command palette.
 * Any change to routes, groups, icons or module gating should happen here so
 * both surfaces stay perfectly in sync.
 *
 * Shape
 *   Two-level shell of `NavGroup`s (Sell / Buy / Stock / Make / Money /
 *   People / Reports / Setup + a bottom "More modules" catch-all). Each
 *   group carries a flat `items: NavItem[]` list plus an optional
 *   `subgroups: NavSubgroup[]` hint that tells the sidebar to render those
 *   items inside labelled sub-dropdowns (used for any group with more than
 *   ~8 items to keep the wall of links scannable). Subgroups reference
 *   items by path — they never store items themselves, so the command
 *   palette can keep walking the flat `items` list unchanged, and disabled
 *   modules are filtered by a single pass over `items`.
 *
 * Plain-language labels
 *   Labels are written for a non-technical shop-floor operator. Every
 *   standard ERP abbreviation is spelled out on first appearance (SO / PO /
 *   GRN / BOM / MRP / UoM / RFQ / POS / P&L / AR); Indian tax acronyms
 *   (GST / TDS) are left as-is since they are the local common form. Each
 *   item carries a one-sentence `description` used as a tooltip so the
 *   operator can peek at what a page does without clicking.
 *
 * Workflow order
 *   Within workflow groups (Sell / Buy / Make / Money / People) the item
 *   order follows the real business flow, so reading the sidebar top-to-
 *   bottom is itself a lightweight training aid.
 *
 * Module gating
 *   `module` is the module key for visibility (`undefined` / `'core'` are
 *   always on; any other value must appear in `useAuth().enabledModules`).
 *   Only routes that exist in `router.tsx` are listed here — dead links
 *   are dropped, not fabricated.
 */
import {
  // Group icons.
  ShoppingCart,
  Truck,
  Package,
  Cpu,
  Factory,
  Banknote,
  Users,
  BarChart3,
  Settings,
  Globe,
  MoreHorizontal,
  // Item icons.
  LayoutDashboard,
  CheckSquare,
  Building2,
  Contact,
  Warehouse,
  Receipt,
  FolderTree,
  Target,
  Activity,
  Kanban,
  FileEdit,
  FileText,
  ClipboardList,
  Boxes,
  ArrowUpDown,
  ArrowLeftRight,
  SlidersHorizontal,
  ClipboardCheck,
  RefreshCw,
  LockKeyhole,
  BookOpen,
  Book,
  Calculator,
  HandCoins,
  Clock,
  Palmtree,
  DollarSign,
  Layers,
  HeartPulse,
  ReceiptText,
  Hourglass,
  PackageMinus,
  PackagePlus,
  PackageSearch,
  Undo2,
  PackageX,
  UserCog,
  Hash,
  Type,
  Car,
  Route,
  FileSignature,
  CalendarClock,
  Headset,
  Wrench,
  MapPinned,
  FolderKanban,
  BadgeCheck,
  AlertTriangle,
  HardHat,
  Store,
  PackageCheck,
  Navigation,
  TrendingUp,
  ShieldCheck,
  Ruler,
  Landmark,
  Upload,
  ScrollText,
  Network,
  Gift,
  Trophy,
  Wallet,
  Tag,
  Percent,
  Award,
  BarChart2,
  Trash,
  Database,
  Download,
  Radio,
  MessageCircle,
  Inbox,
  Megaphone,
  User as UserIcon,
  Gem,
  Scale,
  Hammer,
  PiggyBank,
  Tags,
  ScanLine,
  Link2,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  /** Path passed to react-router-dom NavLink. */
  to: string;
  /** Human-readable label rendered next to the icon (plain English, no jargon). */
  label: string;
  /** lucide-react icon component. */
  icon: LucideIcon;
  /**
   * Module key for visibility gating. `undefined` or `'core'` means always
   * visible; any other value must appear in `useAuth().enabledModules`.
   */
  module?: string;
  /** Small pill on the right of the row (e.g. Approvals: 3). */
  badge?: number | string;
  /**
   * One-sentence plain-language purpose. Rendered as a hover tooltip on the
   * sidebar so a new operator can peek at what a page is for without opening
   * it. Keep it a full sentence, in a customer's voice, not a headline.
   */
  description?: string;
  /**
   * Optional per-role visibility. When set, the item is only shown to
   * users whose Spatie roles list intersects this array. `undefined`
   * means the item is visible to every role (the previous behaviour and
   * still the default for the bulk of the nav). Special value 'Owner'
   * and platform admins are always considered to match — see
   * `filterNav` below.
   */
  roles?: string[];
  /**
   * When set, the item is HIDDEN for users carrying any of these roles
   * (e.g. hide Users/Roles/Setup for Employee and Salesperson). Useful
   * for "everyone except X" rules that would be tedious to spell out
   * with `roles` inclusion lists.
   */
  hideForRoles?: string[];
  /**
   * Shown only to someone who runs the whole install rather than one company
   * on it (`/me.install_admin`). Roles cannot express this: on the shared demo
   * every industry's Owner holds the Owner role, and these pages act on every
   * company at once — the server refuses them, so the nav should not offer them.
   */
  installAdminOnly?: boolean;
  /**
   * Nested items — kept for backward-compat with older consumers that walked
   * the nav tree recursively (command-palette). The current sidebar renders
   * a strict two-level hierarchy and does not populate this field.
   */
  children?: NavItem[];
}

/**
 * A labelled sub-dropdown inside a `NavGroup`. Referenced items are looked
 * up by path from the group's flat `items` list — subgroups never own
 * items themselves, which keeps module filtering and command-palette
 * enumeration a single pass over `items`.
 */
export interface NavSubgroup {
  /** Sub-dropdown heading (e.g. "Order-to-Cash", "Cash & Bank"). */
  label: string;
  /** Optional icon for the sub-dropdown row. */
  icon?: LucideIcon;
  /** Item paths (matching `NavItem.to`) that belong under this sub-dropdown. */
  itemPaths: string[];
  /** Optional one-line hint shown as a tooltip on the sub-dropdown header. */
  description?: string;
}

/**
 * Palette key for a group's accent — drives the icon tint, hover
 * background and active-item ring so a user reading the sidebar
 * top-to-bottom can tell Sell from Buy from Money at a glance.
 * Sidebar picks the actual Tailwind classes; nav.ts just names the key.
 */
export type NavAccent =
  | 'sky' | 'emerald' | 'amber' | 'rose' | 'violet' | 'cyan'
  | 'teal' | 'indigo' | 'pink' | 'orange' | 'slate';

export interface NavGroup {
  /** Top-level group label (renders as the collapsible row heading). */
  label: string;
  /** lucide-react icon shown next to the label. */
  icon: LucideIcon;
  /** Optional accent key. Sidebar tints icons + active row per group. */
  accent?: NavAccent;
  /** Optional tooltip on the group row explaining its purpose. */
  description?: string;
  /** Leaf items shown when the group is expanded. */
  items: NavItem[];
  /**
   * Same semantics as `NavItem.hideForRoles` but at the group level —
   * used to fold away entire groups (e.g. hide Setup from Employee).
   * Applied AFTER item-level filtering so a group with a mix of allowed
   * and disallowed items still collapses cleanly.
   */
  hideForRoles?: string[];
  /**
   * Optional grouping hint. When set, the sidebar renders labelled
   * sub-dropdowns instead of a flat list, using `itemPaths` to pull items
   * out of `items`. Items whose path isn't listed under any subgroup
   * render above the sub-dropdowns as free-standing leaves.
   */
  subgroups?: NavSubgroup[];
  /** When true, the group starts open on first mount (before any user toggle). */
  defaultExpanded?: boolean;
}

/**
 * Backward-compat alias — older code may still import `NavSection`.
 * @deprecated Use `NavGroup`.
 */
export type NavSection = NavGroup;

export const NAV: NavGroup[] = [
  /* --------------------------------------------------------------------- */
  /*  Home                                                                 */
  /* --------------------------------------------------------------------- */
  {
    label: 'Home',
    icon: LayoutDashboard,
    accent: 'sky',
    description: 'Dashboard, approvals waiting on you, and the guided tour.',
    items: [
      {
        to: '/dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        module: 'core',
        description: 'Your daily snapshot: sales today, low stock, jobs running, cash in.',
      },
      {
        to: '/approvals',
        label: 'Approvals',
        icon: CheckSquare,
        module: 'core',
        badge: 3,
        description: 'Things waiting on your OK before they move forward.',
      },
      {
        to: '/self-service',
        label: 'My Self-Service',
        icon: UserIcon,
        module: 'core',
        description: 'Clock in/out, check leaves and payslips, see today\'s tasks.',
        // Owner + platform admins bypass this filter and see everything;
        // Manager/Accountant are business roles that don't need this
        // entry cluttering their sidebar. Salesperson keeps it — they
        // still punch attendance like everyone else.
        roles: ['Employee', 'Salesperson', 'Manager'],
        hideForRoles: ['Accountant'],
      },
      {
        to: '/help',
        label: 'Help & Business Flow',
        icon: FileText,
        module: 'core',
        description: 'How the whole system fits together, with a step-by-step tour.',
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  Buy                                                                  */
  /* --------------------------------------------------------------------- */
  {
    label: 'Buy',
    icon: Truck,
    accent: 'amber',
    description: 'From asking suppliers to paying them.',
    // Employees and Salespeople don't touch the purchase pipeline.
    hideForRoles: ['Employee', 'Salesperson'],
    items: [
      {
        to: '/purchase',
        label: 'Buying Overview',
        icon: LayoutDashboard,
        module: 'purchase',
        description: 'One-screen view of open POs, pending bills, and suppliers you owe.',
      },
      {
        to: '/purchase/requests',
        label: 'Purchase Requests',
        icon: ClipboardList,
        module: 'purchase',
        description: 'Internal request to buy — the first step before suppliers are involved.',
      },
      {
        to: '/purchase/rfqs',
        label: 'RFQs (Request for Quotation)',
        icon: FileSignature,
        module: 'purchase',
        description: 'Ask several suppliers for a price on the same items, then compare what comes back.',
      },
      {
        to: '/purchase/supplier-quotes',
        label: 'Supplier Quotes',
        icon: ScrollText,
        module: 'purchase',
        description: 'Every price a supplier has quoted back against an RFQ.',
      },
      {
        to: '/purchase/orders',
        label: 'Purchase Orders (PO)',
        icon: Truck,
        module: 'purchase',
        description: 'Confirmed order to a supplier.',
      },
      {
        to: '/purchase/grn',
        label: 'Goods Receipts (GRN)',
        icon: PackageCheck,
        module: 'purchase',
        description: 'Goods arriving from a supplier. Adds them to your stock.',
      },
      {
        to: '/purchase/bills',
        label: 'Bills (Purchase Invoices)',
        icon: FileText,
        module: 'purchase',
        description: 'Vendor invoices you owe. Tracked until you pay them.',
      },
      {
        to: '/purchase/debit-notes',
        label: 'Purchase Returns (Debit Notes)',
        icon: Undo2,
        module: 'purchase',
        description: 'Goods sent back to a supplier — reduces what you owe on that bill.',
      },
      {
        to: '/purchase/job-work',
        label: 'Job-work Orders',
        icon: Wrench,
        module: 'purchase',
        description: 'Send raw material out to a vendor for printing / embroidery / lamination, and record what comes back.',
      },
      {
        to: '/suppliers',
        label: 'Suppliers',
        icon: Contact,
        module: 'masterdata',
        description: 'Companies you buy from. Their ledger and contact details.',
      },
      {
        to: '/purchase/evaluation',
        label: 'Supplier Evaluation',
        icon: BadgeCheck,
        module: 'purchase',
        description: 'Score each supplier on price, quality, and on-time delivery.',
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  Stock                                                                */
  /* --------------------------------------------------------------------- */
  {
    label: 'Stock',
    icon: Package,
    accent: 'emerald',
    description: 'What you sell, where it lives, and how it moves.',
    // Employees don't manage stock; hide from them. Salesperson KEEPS
    // this — they need to check what's available before quoting.
    hideForRoles: ['Employee'],
    items: [
      // Catalog.
      {
        to: '/inventory',
        label: 'Stock Overview',
        icon: LayoutDashboard,
        module: 'inventory',
        description: 'One-screen view of what’s in each warehouse right now.',
      },
      {
        to: '/products/raw-materials',
        label: 'Raw Materials',
        icon: PackagePlus,
        module: 'product',
        description: 'What you buy in to make things — ingredients, components, packaging.',
      },
      {
        to: '/products/finished-goods',
        label: 'Finished Goods',
        icon: PackageCheck,
        module: 'product',
        description: 'What you make and sell — the end product a customer receives.',
      },
      {
        to: '/products',
        label: 'All Products',
        icon: Package,
        module: 'product',
        description: 'Everything in one list — raw materials, finished goods, and anything else you track.',
      },
      {
        to: '/masters/categories',
        label: 'Categories',
        icon: FolderTree,
        module: 'masterdata',
        description: 'Groups of similar products, to keep long lists organised.',
      },
      {
        to: '/masters/uoms',
        label: 'Units of Measure (UoM)',
        icon: Ruler,
        module: 'masterdata',
        description: 'How you count and sell things: pieces, kg, litres, boxes.',
      },
      {
        to: '/warehouses',
        label: 'Warehouses',
        icon: Warehouse,
        module: 'masterdata',
        description: 'Physical places you store stock.',
      },

      // Movements & checks.
      {
        to: '/inventory/balances',
        label: 'Stock Balances',
        icon: Boxes,
        module: 'inventory',
        description: 'How much of each product is in each warehouse right now.',
      },
      {
        to: '/inventory/movements',
        label: 'Stock Movements',
        icon: ArrowUpDown,
        module: 'inventory',
        description: 'History of every stock in, out, and transfer.',
      },
      {
        to: '/inventory/transfers',
        label: 'Stock Transfers',
        icon: ArrowLeftRight,
        module: 'inventory',
        description: 'Move stock from one warehouse to another.',
      },
      {
        to: '/inventory/adjustments',
        label: 'Stock Adjustments',
        icon: SlidersHorizontal,
        module: 'inventory',
        description: 'Fix a wrong quantity (breakage, loss, correction).',
      },
      {
        to: '/inventory/cycle-count',
        label: 'Cycle Counts',
        icon: ClipboardCheck,
        module: 'inventory',
        description: 'Regular partial stock-takes to keep the books honest.',
      },
      {
        to: '/inventory/reorder',
        label: 'Reorder Suggestions',
        icon: RefreshCw,
        module: 'inventory',
        description: 'What to buy soon, based on low stock and past sales.',
      },
      {
        to: '/inventory/aging',
        label: 'Stock Aging',
        icon: Hourglass,
        module: 'inventory',
        description: 'How long stock has been sitting. Spots slow-moving items.',
      },
    ],
    subgroups: [
      {
        label: 'Catalog & locations',
        icon: Package,
        description: 'What you sell and where it lives.',
        itemPaths: [
          '/inventory',
          '/products/raw-materials',
          '/products/finished-goods',
          '/products',
          '/masters/categories',
          '/masters/uoms',
          '/warehouses',
        ],
      },
      {
        label: 'Movements & checks',
        icon: ArrowUpDown,
        description: 'How stock moves in, out, and around.',
        itemPaths: [
          '/inventory/balances',
          '/inventory/movements',
          '/inventory/transfers',
          '/inventory/adjustments',
          '/inventory/cycle-count',
          '/inventory/reorder',
          '/inventory/aging',
        ],
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  Make — workflow order                                                */
  /* --------------------------------------------------------------------- */
  {
    label: 'Make',
    icon: Factory,
    accent: 'orange',
    description: 'Manufacturing: recipes, batches, and what to produce next.',
    hideForRoles: ['Employee', 'Salesperson', 'Accountant'],
    items: [
      {
        to: '/manufacturing',
        label: 'Manufacturing Dashboard',
        icon: LayoutDashboard,
        module: 'manufacturing',
        description: 'Live view of jobs on the floor and what’s finished today.',
      },
      {
        to: '/manufacturing/formulas',
        label: 'Formulas',
        icon: Layers,
        module: 'manufacturing',
        description: 'Recipes for batch or continuous production — use BOMs for discrete assembly.',
      },
      {
        to: '/manufacturing/process-templates',
        label: 'Process Templates',
        icon: ClipboardList,
        module: 'manufacturing',
        description: 'The list of steps to walk through when making a product.',
      },
      {
        to: '/manufacturing/boms',
        label: 'Item Recipes (BOM)',
        icon: Layers,
        module: 'manufacturing',
        description: 'Bills of Material — the raw materials that make one finished item.',
      },
      {
        to: '/manufacturing/production-orders',
        label: 'Production Orders',
        icon: ClipboardList,
        module: 'manufacturing',
        description: 'One batch you are making right now, or planned to make.',
      },
      {
        to: '/manufacturing/board',
        label: 'Production Board',
        icon: Kanban,
        module: 'manufacturing',
        description: 'Kanban of every active batch, grouped by the stage it is at right now. Spot holds, hand-offs, and stages running late.',
      },
      {
        to: '/manufacturing/live',
        label: 'Shop Floor Live',
        icon: Radio,
        module: 'manufacturing',
        description: 'Every active batch at a glance — current step, elapsed time, holds. No clicking into each one.',
      },
      {
        to: '/manufacturing/mrp',
        label: 'Material Planning (MRP)',
        icon: Boxes,
        module: 'manufacturing',
        description: 'Suggests what to make and what to buy, based on demand vs on-hand.',
      },
      {
        to: '/manufacturing/work-centres',
        label: 'Work Centres',
        icon: Factory,
        module: 'manufacturing',
        description: 'Every station on the floor — its machines, its hourly rate, and what is running on it right now.',
      },
      {
        to: '/manufacturing/machines',
        label: 'Machines',
        icon: Cpu,
        module: 'manufacturing',
        description: 'The asset register: which machines are running, idle, in maintenance or broken down.',
      },
      {
        to: '/manufacturing/material-issues',
        label: 'Material Issues',
        icon: PackageMinus,
        module: 'manufacturing',
        description: 'Raw material drawn out of stock and consumed by production, with the value it took off the shelf.',
      },
      {
        to: '/manufacturing/finished-goods-receipts',
        label: 'Finished Goods Receipts',
        icon: PackagePlus,
        module: 'manufacturing',
        description: 'Finished output booked back into stock off each batch, with scrap and yield.',
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  Sell — Sales Pipeline / Order-to-Cash / People & Rules               */
  /*  Deliberately last in the operational chain: you buy raw material,    */
  /*  stock it, make the product, THEN sell it. Nav order mirrors that.    */
  /* --------------------------------------------------------------------- */
  {
    label: 'Sell',
    icon: ShoppingCart,
    accent: 'violet',
    description: 'Everything from a fresh lead to money in the bank.',
    items: [
      // Sales pipeline.
      {
        to: '/crm',
        label: 'Sales Overview',
        icon: LayoutDashboard,
        module: 'crm',
        description: 'One-screen view of leads, deals, and today’s sales activity.',
      },
      {
        to: '/crm/leads',
        label: 'Leads',
        icon: Users,
        module: 'crm',
        description: 'People who might buy. Not yet a real deal.',
      },
      {
        to: '/crm/opportunities',
        label: 'Opportunities',
        icon: Target,
        module: 'crm',
        description: 'A real deal in progress. Has a value and a close date.',
      },
      {
        to: '/crm/activities',
        label: 'Activities',
        icon: Activity,
        module: 'crm',
        description: 'Calls, meetings, and follow-ups scheduled with your customers.',
      },
      {
        to: '/crm/leads/pipeline',
        label: 'Lead Pipeline Board',
        icon: Kanban,
        module: 'crm',
        description: 'Drag-and-drop board showing where each lead sits in the funnel.',
      },
      {
        to: '/crm/opportunities/pipeline',
        label: 'Opportunity Pipeline Board',
        icon: Kanban,
        module: 'crm',
        description: 'Drag-and-drop board of live deals, by stage.',
      },
      {
        to: '/sales/my-pipeline',
        label: 'My Pipeline',
        icon: Kanban,
        module: 'sales',
        description: 'Just the deals owned by you — filtered from the shared board.',
      },
      {
        to: '/customers',
        label: 'Customers',
        icon: Contact,
        module: 'masterdata',
        description: 'People and companies you sell to. Their ledger lives here too.',
      },

      // Order-to-Cash.
      {
        to: '/sales/quotations',
        label: 'Quotations',
        icon: FileEdit,
        module: 'sales',
        description: 'Prices you sent to a customer, before they say yes.',
      },
      {
        to: '/sales/orders',
        label: 'Sales Orders (SO)',
        icon: ShoppingCart,
        module: 'sales',
        description: 'Confirmed customer commitment. Turns into a delivery and an invoice.',
      },
      {
        to: '/sales/deliveries',
        label: 'Deliveries',
        icon: Truck,
        module: 'sales',
        description: 'Pack and dispatch a confirmed order — stock leaves the warehouse here.',
      },
      {
        to: '/sales/invoices',
        label: 'Sales Invoices',
        icon: Receipt,
        module: 'sales',
        description: 'The bill you sent the customer. Tracked until it’s paid.',
      },
      {
        to: '/sales/credit-notes',
        label: 'Sales Returns (Credit Notes)',
        icon: Undo2,
        module: 'sales',
        description: 'Goods a customer sent back — reduces what they owe on that invoice.',
      },
      {
        to: '/sales/customer-statement',
        label: 'Customer Statement',
        icon: FileText,
        module: 'sales',
        description: 'A printable ledger of everything a customer bought and paid.',
      },

      // People & rules.
      {
        to: '/sales/salespersons',
        label: 'Salespersons',
        icon: Contact,
        module: 'sales',
        description: 'The people on your sales team, and who owns which customers.',
      },
      {
        to: '/sales/territories',
        label: 'Territories',
        icon: MapPinned,
        module: 'sales',
        description: 'Regions carved up between salespersons.',
      },
      {
        to: '/sales/pricing-schemes',
        label: 'Pricing Rules',
        icon: Tag,
        module: 'sales',
        description: 'Discounts and price lists applied automatically at quote time.',
      },
      {
        to: '/sales/commission-schemes',
        label: 'Commission Schemes',
        icon: Percent,
        module: 'sales',
        description: 'The rules that decide how much commission each sale earns.',
      },
      {
        to: '/sales/salesperson-targets',
        label: 'Sales Targets',
        icon: Target,
        module: 'sales',
        description: 'Time-boxed goals per salesperson, with optional bonus attached.',
      },
      {
        to: '/sales/commission-earnings',
        label: 'Commission Earnings',
        icon: HandCoins,
        module: 'sales',
        description: 'How much each salesperson has earned this period.',
      },
      {
        to: '/sales/leaderboard',
        label: 'Sales Leaderboard',
        icon: Trophy,
        module: 'sales',
        description: 'Ranking of who sold the most this month.',
      },
    ],
    subgroups: [
      {
        label: 'Sales pipeline',
        icon: Target,
        description: 'From a fresh lead to a signed opportunity.',
        itemPaths: [
          '/crm',
          '/crm/leads',
          '/crm/opportunities',
          '/crm/activities',
          '/crm/leads/pipeline',
          '/crm/opportunities/pipeline',
          '/sales/my-pipeline',
          '/customers',
        ],
      },
      {
        label: 'Order-to-Cash',
        icon: ShoppingCart,
        description: 'Quote → order → invoice → payment.',
        itemPaths: [
          '/sales/quotations',
          '/sales/orders',
          '/sales/deliveries',
          '/sales/invoices',
          '/sales/credit-notes',
          '/sales/customer-statement',
        ],
      },
      {
        label: 'People & rules',
        icon: Users,
        description: 'Salespersons, territories, pricing, and commissions.',
        itemPaths: [
          '/sales/salespersons',
          '/sales/territories',
          '/sales/pricing-schemes',
          '/sales/commission-schemes',
          '/sales/salesperson-targets',
          '/sales/commission-earnings',
          '/sales/leaderboard',
        ],
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  Jewellery — vertical module.                                         */
  /*                                                                       */
  /*  Sits between Sell and WhatsApp because it IS the selling side of a   */
  /*  jewellery business, not a back-office add-on: the rate board and the */
  /*  tag lookup are counter tools. Gated on the `jewellery` module key,   */
  /*  so no other vertical ever sees the group.                            */
  /* --------------------------------------------------------------------- */
  {
    label: 'Jewellery',
    icon: Gem,
    accent: 'amber',
    description: 'Rates, tagged pieces, karigar hisab, schemes and girvi.',
    items: [
      {
        to: '/jewellery',
        label: 'Jewellery Overview',
        icon: LayoutDashboard,
        module: 'jewellery',
        hideForRoles: ['Employee', 'Salesperson'],
        description: 'Metal on the shelf, out with karigars, owed on schemes, and lent against pledges.',
      },
      {
        to: '/jewellery/rates',
        label: 'Rate Board (Bhav)',
        icon: Scale,
        module: 'jewellery',
        description: "Today's gold and silver rates, and how they have moved.",
      },
      {
        to: '/jewellery/items',
        label: 'Tagged Stock',
        icon: Tags,
        module: 'jewellery',
        description: 'Every physical piece — its weight, purity, HUID and where it is.',
      },
      {
        to: '/jewellery/lookup',
        label: 'Tag Lookup',
        icon: ScanLine,
        module: 'jewellery',
        description: 'Scan or type a tag, HUID or certificate to pull up one piece.',
      },
      {
        to: '/jewellery/estimate',
        label: 'Price Estimate',
        icon: Calculator,
        module: 'jewellery',
        hideForRoles: ['Employee'],
        description: 'Weight, wastage, making and old gold — what the customer actually pays.',
      },
      {
        to: '/jewellery/karigars',
        label: 'Karigars & Hisab',
        icon: Hammer,
        module: 'jewellery',
        hideForRoles: ['Employee', 'Salesperson'],
        description: 'Which goldsmith holds how much of your metal.',
      },
      {
        to: '/jewellery/karigar-jobs',
        label: 'Karigar Jobs',
        icon: ClipboardList,
        module: 'jewellery',
        hideForRoles: ['Employee', 'Salesperson'],
        description: 'Metal issued, work received, wastage and what is overdue.',
      },
      {
        to: '/jewellery/schemes',
        label: 'Saving Schemes',
        icon: PiggyBank,
        module: 'jewellery',
        hideForRoles: ['Employee'],
        description: 'Gold kitty accounts, instalments taken, and grams owed at maturity.',
      },
      {
        to: '/jewellery/girvi',
        label: 'Girvi (Pledge Loans)',
        icon: HandCoins,
        module: 'jewellery',
        hideForRoles: ['Employee', 'Salesperson'],
        description: 'Money lent against ornaments, interest accrued and what is overdue.',
      },
      {
        to: '/jewellery/reports/stock',
        label: 'Metal Stock Report',
        icon: BarChart3,
        module: 'jewellery',
        hideForRoles: ['Employee', 'Salesperson'],
        description: 'Closing stock in fine grams by purity, outlet and design.',
      },
      {
        to: '/jewellery/reports/hallmarking',
        label: 'HUID Coverage',
        icon: ShieldCheck,
        module: 'jewellery',
        hideForRoles: ['Employee'],
        description: 'Which hallmarked pieces still have no HUID on file.',
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  WhatsApp — addon module.                                             */
  /*  Sits right after Sell: it is a selling channel, not a back-office    */
  /*  function. Gated on the `whatsapp_marketing` module key, so tenants   */
  /*  without the addon never see the group at all.                        */
  /* --------------------------------------------------------------------- */
  {
    label: 'WhatsApp',
    icon: MessageCircle,
    accent: 'emerald',
    description: 'Talk to customers on WhatsApp — campaigns, replies, and who read what.',
    items: [
      {
        to: '/whatsapp',
        label: 'WhatsApp Overview',
        icon: LayoutDashboard,
        module: 'whatsapp_marketing',
        description: 'How much you sent today, what got delivered, and which campaigns are running.',
      },
      {
        to: '/whatsapp/inbox',
        label: 'Inbox',
        icon: Inbox,
        module: 'whatsapp_marketing',
        description: 'Two-way chat with anyone who messages your WhatsApp number.',
      },
      {
        to: '/whatsapp/contacts',
        label: 'Contacts',
        icon: Contact,
        module: 'whatsapp_marketing',
        description: 'Everyone you can message, pulled in from your customers and suppliers.',
      },
      {
        to: '/whatsapp/groups',
        label: 'Contact Groups',
        icon: FolderTree,
        module: 'whatsapp_marketing',
        description: 'Bundles of contacts so a campaign can reach the right audience in one shot.',
      },
      {
        to: '/whatsapp/templates',
        label: 'Templates',
        icon: FileText,
        module: 'whatsapp_marketing',
        description: 'Pre-approved messages from Meta — the only thing a campaign is allowed to send.',
      },
      {
        to: '/whatsapp/campaigns',
        label: 'Campaigns',
        icon: Megaphone,
        module: 'whatsapp_marketing',
        description: 'Send a template to a whole group, now or on a schedule.',
      },
      {
        to: '/whatsapp/reports',
        label: 'Message Reports',
        icon: BarChart2,
        module: 'whatsapp_marketing',
        description: 'Every message sent or received, with what happened to it.',
      },
      {
        to: '/whatsapp/settings',
        label: 'WhatsApp Settings',
        icon: Settings,
        module: 'whatsapp_marketing',
        description: 'Your plan, and the WhatsApp number this company sends from.',
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  Money                                                                */
  /* --------------------------------------------------------------------- */
  {
    label: 'Money',
    icon: Banknote,
    accent: 'teal',
    description: 'Accounting: ledger, banks, and statutory reports.',
    hideForRoles: ['Employee', 'Salesperson'],
    items: [
      // Ledger & books.
      {
        to: '/accounting',
        label: 'Accounting Overview',
        icon: LayoutDashboard,
        module: 'accounting',
        description: 'One-screen view of cash, receivables, and payables.',
      },
      {
        to: '/accounting/coa',
        label: 'Chart of Accounts',
        icon: BookOpen,
        module: 'accounting',
        description: 'Your ledger structure. Every posting hits one of these accounts.',
      },
      {
        to: '/accounting/journal',
        label: 'Journal Entries',
        icon: Book,
        module: 'accounting',
        description: 'Manual accounting entries: adjustments, opening balances, corrections.',
      },
      {
        to: '/accounting/trial-balance',
        label: 'Trial Balance',
        icon: Calculator,
        module: 'accounting',
        description: 'Debit totals vs credit totals for every account. First stop before finalising books.',
      },
      {
        to: '/accounting/budgets',
        label: 'Budgets',
        icon: Wallet,
        module: 'accounting',
        description: 'Set per-account annual targets, month by month if you need an uneven spread.',
      },
      {
        to: '/masters/taxes',
        label: 'Taxes',
        icon: Receipt,
        module: 'masterdata',
        description: 'Tax rates (GST, TDS) you charge or withhold on documents.',
      },

      // Cash & bank.
      {
        to: '/accounting/bank-accounts',
        label: 'Bank Accounts',
        icon: Landmark,
        module: 'accounting',
        description: 'Your bank accounts, opening balances, and current balance in the books.',
      },
      {
        to: '/accounting/bank-reconciliation',
        label: 'Bank Reconciliation',
        icon: ArrowLeftRight,
        module: 'accounting',
        description: 'Match the bank statement against your books. Catch missing entries.',
      },
      {
        to: '/accounting/receipts-payments',
        label: 'Receipts (Payments)',
        icon: HandCoins,
        module: 'accounting',
        description: 'Record money in from customers or out to suppliers.',
      },
      {
        to: '/accounting/tally-export',
        label: 'Tally Export',
        icon: Download,
        module: 'accounting',
        description: 'Download vouchers as a Tally-ready XML file, ready to import into Tally in one click.',
      },
    ],
    subgroups: [
      {
        label: 'Ledger & books',
        icon: BookOpen,
        description: 'The accounting core.',
        itemPaths: [
          '/accounting',
          '/accounting/coa',
          '/accounting/journal',
          '/accounting/trial-balance',
          '/accounting/budgets',
          '/masters/taxes',
        ],
      },
      {
        label: 'Cash & bank',
        icon: Landmark,
        description: 'Bank accounts and payment postings.',
        itemPaths: [
          '/accounting/bank-accounts',
          '/accounting/bank-reconciliation',
          '/accounting/receipts-payments',
          '/accounting/tally-export',
        ],
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  People — workflow order                                              */
  /* --------------------------------------------------------------------- */
  {
    label: 'People',
    icon: Users,
    accent: 'pink',
    description: 'HR: employees, attendance, leave, and payroll.',
    items: [
      {
        to: '/hr',
        label: 'HR Overview',
        icon: LayoutDashboard,
        module: 'hr',
        description: 'Headcount, joiners this month, leave taken, payroll due.',
      },
      {
        to: '/hr/employees',
        label: 'Employees',
        icon: Users,
        module: 'hr',
        description: 'Every person on the payroll. Their contract, salary, and documents.',
      },
      {
        to: '/hr/org-chart',
        label: 'Org Chart',
        icon: Network,
        module: 'hr',
        description: 'Who reports to whom, in a tree.',
      },
      {
        to: '/expense/claims',
        label: 'Expense Claims',
        icon: Receipt,
        module: 'expense',
        description: 'Employee reimbursement claims — submit, approve, and pay out.',
      },
      {
        to: '/expense/advances',
        label: 'Expense Advances',
        icon: Wallet,
        module: 'expense',
        description: 'Cash advances given to employees, tracked until settled.',
      },
      {
        to: '/expense/categories',
        label: 'Expense Categories',
        icon: Tag,
        module: 'expense',
        description: 'The category list employees choose from when filing a claim.',
      },
      {
        to: '/attendance',
        label: 'Attendance',
        icon: Clock,
        module: 'attendance',
        description: 'Daily punch-in/punch-out records for every employee.',
      },
      {
        to: '/attendance/calendar',
        label: 'Attendance Calendar',
        icon: CalendarClock,
        module: 'attendance',
        description: 'Month view of who was present, absent, or on leave.',
      },
      {
        to: '/leave',
        label: 'Leave Approvals',
        icon: Palmtree,
        module: 'leave',
        description: 'Leave applications and approvals. Balances per employee.',
      },
      {
        to: '/leave/apply',
        label: 'Apply for Leave',
        icon: FileEdit,
        module: 'leave',
        description: 'Raise a leave application — for yourself, or on behalf of any employee if you are an admin.',
      },
      {
        to: '/leave/team-calendar',
        label: 'Team Calendar',
        icon: CalendarClock,
        module: 'leave',
        description: 'Month view of who is away, so you can plan cover before approving.',
      },
      {
        to: '/payroll',
        label: 'Payroll',
        icon: DollarSign,
        module: 'payroll',
        description: 'Run monthly payroll: gross, deductions, net pay, payslips.',
      },
      {
        to: '/payroll/salary-structures',
        label: 'Salary Structures',
        icon: Wallet,
        module: 'payroll',
        description: 'The template of components (basic, HRA, tax) that builds each payslip.',
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  Reports                                                              */
  /* --------------------------------------------------------------------- */
  {
    label: 'Reports',
    icon: BarChart3,
    accent: 'indigo',
    description: 'Business intelligence and statutory reports.',
    items: [
      // Money & customers.
      {
        to: '/reports/health',
        label: 'Business Health',
        icon: HeartPulse,
        module: 'core',
        description: 'One-page pulse check: sales, cash, margin, outstanding.',
      },
      {
        to: '/reports/pnl',
        label: 'Profit & Loss (P&L)',
        icon: TrendingUp,
        module: 'accounting',
        description: 'Did you make money this month? Revenue minus expenses.',
      },
      {
        to: '/reports/sales-register',
        label: 'Sales Register',
        icon: ReceiptText,
        module: 'sales',
        description: 'Every sales invoice raised in a period, with tax breakup.',
      },
      {
        to: '/reports/purchase-register',
        label: 'Purchase Register',
        icon: Truck,
        module: 'purchase',
        description: 'Every purchase bill received in a period, with tax breakup.',
      },
      {
        to: '/reports/customer-aging',
        label: 'Customer Aging (AR)',
        icon: Hourglass,
        module: 'accounting',
        description: 'Who owes you money, and how overdue they are.',
      },
      {
        to: '/reports/sales-margin',
        label: 'Sales Margin',
        icon: Percent,
        module: 'sales',
        description: 'Selling price minus cost, per product and per invoice.',
      },
      {
        to: '/reports/salesperson-performance',
        label: 'Salesperson Performance',
        icon: Award,
        module: 'sales',
        description: 'Sales, margin, and commission earned by each salesperson.',
      },
      {
        to: '/reports/outlet-performance',
        label: 'Outlet Performance',
        icon: Store,
        module: 'pos',
        description: 'Every retail outlet ranked by till takings, against the previous period.',
      },
      {
        to: '/reports/budget-vs-actual',
        label: 'Budget vs Actual',
        icon: Wallet,
        module: 'accounting',
        description: 'What you planned per account against what actually posted to the ledger.',
      },

      // Manufacturing & stock.
      {
        to: '/reports/stock-valuation',
        label: 'Stock Valuation',
        icon: PackageSearch,
        module: 'inventory',
        description: 'What your on-hand stock is worth, at cost, right now.',
      },
      {
        to: '/reports/batch-cost',
        label: 'Batch Cost Report',
        icon: Receipt,
        module: 'manufacturing',
        description: 'Actual cost of each production batch: material + labour + overhead.',
      },
      {
        to: '/reports/product-cost-trend',
        label: 'Product Cost Trend',
        icon: TrendingUp,
        module: 'manufacturing',
        description: 'How the cost of a product has moved over time.',
      },
      {
        to: '/reports/material-usage',
        label: 'Material Usage',
        icon: BarChart2,
        module: 'manufacturing',
        description: 'How much of each raw material was consumed, per product.',
      },
      {
        to: '/reports/waste',
        label: 'Waste Report',
        icon: Trash,
        module: 'manufacturing',
        description: 'Scrap and rework logged during production.',
      },
      {
        to: '/reports/rejections',
        label: 'Raw Material Rejections',
        icon: PackageX,
        module: 'purchase',
        description: 'Material that arrived but failed inspection, and what it cost you.',
      },
      {
        to: '/reports/yield',
        label: 'Yield & On-Time Report',
        icon: Target,
        module: 'manufacturing',
        description: 'Plan vs actual: how much came out, and whether it finished on time.',
      },
    ],
    subgroups: [
      {
        label: 'Money & customers',
        icon: Banknote,
        description: 'Sales, margin, receivables and P&L.',
        itemPaths: [
          '/reports/health',
          '/reports/pnl',
          '/reports/sales-register',
          '/reports/purchase-register',
          '/reports/customer-aging',
          '/reports/sales-margin',
          '/reports/salesperson-performance',
          '/reports/budget-vs-actual',
        ],
      },
      {
        label: 'Manufacturing & stock',
        icon: Factory,
        description: 'Costing, stock value, and production quality.',
        itemPaths: [
          '/reports/stock-valuation',
          '/reports/batch-cost',
          '/reports/product-cost-trend',
          '/reports/material-usage',
          '/reports/waste',
          '/reports/rejections',
          '/reports/yield',
        ],
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  Setup                                                                */
  /* --------------------------------------------------------------------- */
  {
    label: 'Setup',
    icon: Settings,
    accent: 'slate',
    description: 'Company details, access control, and system preferences.',
    // Only Owner (bypasses the filter) and Manager see the Setup group.
    // Accountant, Salesperson, and Employee do NOT — they can't change
    // anything here anyway and the noise is confusing on the demo.
    hideForRoles: ['Employee', 'Salesperson', 'Accountant'],
    items: [
      // Company & access.
      {
        to: '/organization/companies',
        label: 'Company',
        icon: Building2,
        module: 'organization',
        description: 'Your legal entity: name, GSTIN, PAN, logo, address.',
      },
      {
        to: '/organization/branches',
        label: 'Branches',
        icon: Store,
        module: 'organization',
        description: 'Multiple physical locations of the same company.',
      },
      {
        to: '/organization/departments',
        label: 'Departments',
        icon: FolderKanban,
        module: 'organization',
        description: 'Internal divisions used for org chart and expense tagging.',
      },
      {
        to: '/admin/users',
        label: 'Users & Roles',
        icon: UserCog,
        module: 'core',
        description: 'Who can log in, and what each person is allowed to do.',
      },
      // System preferences.
      {
        to: '/admin/global-settings',
        label: 'Global Settings',
        icon: Globe,
        module: 'core',
        description: 'Tenant-wide defaults: currency, country, timezone, date & number formats.',
      },
      {
        to: '/admin/numbering',
        label: 'Document Numbering',
        icon: Hash,
        module: 'core',
        description: 'Prefixes and starting numbers for SO, PO, invoices, etc.',
      },
      {
        to: '/admin/batch-numbering',
        label: 'Batch Numbering',
        icon: Hash,
        module: 'core',
        description: 'How production batch numbers are generated.',
      },
      {
        to: '/admin/terminology',
        label: 'Terminology',
        icon: Type,
        module: 'core',
        description: 'Rename things to match your trade (e.g. "Job" instead of "Order").',
      },
      {
        to: '/admin/imports',
        label: 'Import Data',
        icon: Upload,
        module: 'core',
        description: 'Bulk import customers, products, or opening balances from a spreadsheet.',
      },
      {
        to: '/admin/audit',
        label: 'Audit Log',
        icon: ScrollText,
        module: 'core',
        description: 'A tamper-proof history of who changed what, and when.',
        // The full feed spans every company; an industry Owner on a shared
        // install reads each record's own history from the record instead.
        installAdminOnly: true,
      },
      {
        to: '/admin/sessions',
        label: 'Active Sessions',
        icon: LockKeyhole,
        module: 'core',
        description: 'Who is currently logged in, on which device.',
      },
      {
        to: '/admin/login-history',
        label: 'Login History',
        icon: Clock,
        module: 'core',
        description: 'Past login attempts — successful and failed.',
        // The API serves this to owners and administrators only.
        roles: ['admin'],
      },
      {
        to: '/admin/demo-data',
        label: 'Demo Data',
        icon: Database,
        module: 'core',
        description: 'Pick an industry demo dataset, or reset everything and start fresh.',
        installAdminOnly: true,
      },
      {
        to: '/admin/login-links',
        label: 'Sign-in Links',
        icon: Link2,
        module: 'core',
        description: 'One link per industry. Each opens only that company, with no switching.',
        installAdminOnly: true,
      },
    ],
    subgroups: [
      {
        label: 'Company & access',
        icon: Building2,
        description: 'Legal entity, users, and approval rules.',
        itemPaths: [
          '/organization/companies',
          '/organization/branches',
          '/organization/departments',
          '/admin/users',
        ],
      },
      {
        label: 'System preferences',
        icon: Settings,
        description: 'Numbering, imports, and security housekeeping.',
        itemPaths: [
          '/admin/numbering',
          '/admin/batch-numbering',
          '/admin/terminology',
          '/admin/imports',
          '/admin/audit',
          '/admin/sessions',
          '/admin/login-history',
          '/admin/demo-data',
        ],
      },
    ],
  },

  /* --------------------------------------------------------------------- */
  /*  More modules                                                         */
  /* --------------------------------------------------------------------- */
  {
    label: 'More modules',
    icon: MoreHorizontal,
    accent: 'cyan',
    description: 'Retail, online, service, projects, fleet, and other add-ons.',
    items: [
      // Retail & online.
      {
        to: '/pos',
        label: 'Point of Sale (POS)',
        icon: LayoutDashboard,
        module: 'pos',
        description: 'Counter-sales dashboard: today’s takings and open cash drawers.',
      },
      {
        to: '/pos/sessions',
        label: 'POS Sessions',
        icon: LockKeyhole,
        module: 'pos',
        description: 'Open and close a cash drawer for a shift.',
      },
      {
        to: '/pos/sales-history',
        label: 'POS Sales History',
        icon: Receipt,
        module: 'pos',
        description: 'Every counter-sale receipt, searchable by date or cashier.',
      },
      {
        to: '/pos/loyalty-gift-cards',
        label: 'Loyalty & Gift Cards',
        icon: Gift,
        module: 'pos',
        description: 'Customer loyalty points and gift-card balances.',
      },
      {
        to: '/logistics/shipments',
        label: 'Shipments',
        icon: Navigation,
        module: 'logistics',
        description: 'Outbound courier shipments with tracking numbers.',
      },
      {
        to: '/contract',
        label: 'Contracts',
        icon: FileSignature,
        module: 'contract',
        description: 'Long-running customer or supplier agreements with renewal dates.',
      },
      {
        to: '/rental',
        label: 'Rentals',
        icon: CalendarClock,
        module: 'rental',
        description: 'Bookings for equipment or items you rent out by the day.',
      },
      {
        to: '/compliance',
        label: 'Compliance Documents',
        icon: ShieldCheck,
        module: 'compliance',
        description: 'Licences, certifications, and their expiry alerts.',
      },

      // Assets, service & projects.
      {
        to: '/fleet/vehicles',
        label: 'Fleet Vehicles',
        icon: Car,
        module: 'fleet',
        description: 'Vehicles you own: registration, insurance, service history.',
      },
      {
        to: '/fleet/trips',
        label: 'Fleet Trips',
        icon: Route,
        module: 'fleet',
        description: 'Every trip a vehicle made, with driver and distance.',
      },
      {
        to: '/maintenance/assets',
        label: 'Assets',
        icon: HardHat,
        module: 'maintenance',
        description: 'Machines and equipment you track for maintenance.',
      },
      {
        to: '/maintenance/requests',
        label: 'Maintenance Requests',
        icon: Wrench,
        module: 'maintenance',
        description: 'Requests to fix or service an asset.',
      },
      {
        to: '/service/tickets',
        label: 'Support Tickets',
        icon: Headset,
        module: 'service',
        description: 'Customer complaints and support cases.',
      },
      {
        to: '/service/visits',
        label: 'Service Visits',
        icon: Wrench,
        module: 'service',
        description: 'Scheduled on-site service calls to a customer.',
      },
      {
        to: '/fieldservice',
        label: 'Field Service',
        icon: MapPinned,
        module: 'fieldservice',
        description: 'Dispatch board for engineers out on the road.',
      },
      {
        to: '/quality/inspections',
        label: 'Quality Inspections',
        icon: BadgeCheck,
        module: 'quality',
        description: 'QC checks against a checklist, pass/fail per lot.',
      },
      {
        to: '/quality/non-conformances',
        label: 'Non-Conformances',
        icon: AlertTriangle,
        module: 'quality',
        description: 'Items that failed QC and what happened next.',
      },
      {
        to: '/projects',
        label: 'Projects',
        icon: FolderKanban,
        module: 'projects',
        description: 'Long-running work with tasks, deadlines, and a budget.',
      },
    ],
    subgroups: [
      {
        label: 'Retail & online',
        icon: Store,
        description: 'Counter sales, online orders, shipments and contracts.',
        itemPaths: [
          '/pos',
          '/pos/sessions',
          '/pos/sales-history',
          '/pos/loyalty-gift-cards',
          '/logistics/shipments',
          '/contract',
          '/rental',
          '/compliance',
        ],
      },
      {
        label: 'Assets, service & projects',
        icon: Wrench,
        description: 'Fleet, machines, service calls, quality, and project work.',
        itemPaths: [
          '/fleet/vehicles',
          '/fleet/trips',
          '/maintenance/assets',
          '/maintenance/requests',
          '/service/tickets',
          '/service/visits',
          '/fieldservice',
          '/quality/inspections',
          '/quality/non-conformances',
          '/projects',
        ],
      },
    ],
  },
];

/**
 * Returns true if a nav item's module is enabled for the current session.
 * Items with no module, or with module = 'core', are always considered enabled.
 */
export function isNavItemEnabled(item: NavItem, enabledModules: readonly string[]): boolean {
  if (!item.module || item.module === 'core') return true;
  return enabledModules.includes(item.module);
}

/**
 * Returns true if the current user's roles are allowed to see the item.
 * Owner and any Spatie super-admin role (`super-admin`, `admin`) bypass
 * the check — they're deliberately unrestricted so a demo owner never
 * loses a page just because the item's role list happens to miss it.
 */
export function isNavItemVisibleForRoles(
  item: NavItem,
  roles: readonly string[],
): boolean {
  const isPrivileged = roles.includes('Owner')
    || roles.includes('super-admin')
    || roles.includes('admin');

  // Deny list wins over allow list — a privileged user still respects
  // hideForRoles if they somehow match it (unusual but defensible).
  if (item.hideForRoles && item.hideForRoles.some((r) => roles.includes(r))) {
    return false;
  }
  if (isPrivileged) return true;

  // A counter cashier is the one role that is restricted by DEFAULT rather
  // than by opt-in. Everywhere else an item with no `roles` is visible to
  // everyone, which is the right default for office staff — but it would hand
  // the person on the till the whole of Manufacturing, Accounting and HR in
  // their sidebar. They cannot read another outlet's data (BranchScope sees to
  // that), yet a menu full of pages they must never touch is its own hazard on
  // a shared tablet. So: a cashier sees the till and the things a till needs.
  if (isCashierOnly(roles)) {
    return CASHIER_PATHS.some((prefix) => item.to === prefix || item.to.startsWith(prefix + '/'));
  }

  if (!item.roles || item.roles.length === 0) return true;
  return item.roles.some((r) => roles.includes(r));
}

/**
 * Paths a counter cashier may see. Prefix match, so `/pos` covers the
 * terminal, the sessions list and the sales history beneath it.
 */
const CASHIER_PATHS: readonly string[] = [
  '/pos',
  '/self-service',
  '/products',
  '/stock/balances',
];

/** True when the session is a cashier and nothing more senior. */
function isCashierOnly(roles: readonly string[]): boolean {
  if (!roles.includes('Cashier')) return false;
  const senior = ['Owner', 'Manager', 'Accountant', 'admin', 'super-admin'];
  return !senior.some((r) => roles.includes(r));
}

/**
 * Filter the whole nav tree down to what the current session should see:
 * hides disabled items, drops empty groups, and prunes any `subgroup`
 * whose referenced items are all gone.
 *
 * Roles are opt-in — the previous call sites (which passed only
 * enabledModules) still work: with no roles supplied, role filtering is
 * a no-op.
 */
export function filterNav(
  groups: readonly NavGroup[],
  enabledModules: readonly string[],
  roles: readonly string[] = [],
  opts: { installAdmin?: boolean } = {},
): NavGroup[] {
  const visible = (i: NavItem) =>
    isNavItemEnabled(i, enabledModules)
    && isNavItemVisibleForRoles(i, roles)
    && (!i.installAdminOnly || !!opts.installAdmin);
  return groups
    .map((group) => {
      const items = group.items
        .filter(visible)
        .map((i) => ({
          ...i,
          children: i.children?.filter(visible),
        }));

      // Prune subgroups down to only the paths that still exist in `items`.
      // A subgroup with nothing left is dropped entirely.
      const visiblePaths = new Set(items.map((i) => i.to));
      const subgroups = group.subgroups
        ?.map((sg) => ({
          ...sg,
          itemPaths: sg.itemPaths.filter((p) => visiblePaths.has(p)),
        }))
        .filter((sg) => sg.itemPaths.length > 0);

      return {
        ...group,
        items,
        subgroups,
      };
    })
    .filter((group) => {
      if (group.items.length === 0) return false;
      const isPrivileged = roles.includes('Owner')
        || roles.includes('super-admin')
        || roles.includes('admin');
      if (isPrivileged) return true;
      if (group.hideForRoles && group.hideForRoles.some((r) => roles.includes(r))) {
        return false;
      }
      return true;
    });
}
